import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from './components/Layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import { NavigationTracker } from './components/NavigationTracker'
import { AuthProvider } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { HelpProvider } from './context/HelpContext'
import { TourProvider } from './context/TourContext'
import { ToastProvider } from './context/ToastContext'
import { WorkspaceHeaderProvider } from './context/WorkspaceHeaderContext'
import { TopologyHeaderProvider } from './context/TopologyHeaderContext'
const HelpPanel = lazy(() => import('./components/help/HelpPanel').then(m => ({ default: m.HelpPanel })))

// Public pages - loaded immediately (entry points)
import { LoginPage } from './pages/Login'
import { SetupPage } from './pages/Setup'
import { PublicEventView } from './pages/PublicEventView'

// Protected pages - lazy loaded for better initial bundle size
const EventsPage = lazy(() => import('./pages/Events').then(m => ({ default: m.EventsPage })))
const LiveTailPage = lazy(() => import('./pages/LiveTail').then(m => ({ default: m.LiveTailPage })))
const SignalsPage = lazy(() => import('./pages/Signals').then(m => ({ default: m.SignalsPage })))
const DashboardsPage = lazy(() => import('./pages/Dashboards').then(m => ({ default: m.DashboardsPage })))
const DashboardViewPage = lazy(() => import('./pages/DashboardView').then(m => ({ default: m.DashboardViewPage })))
const AlertsPage = lazy(() => import('./pages/Alerts').then(m => ({ default: m.AlertsPage })))
const AnomaliesPage = lazy(() => import('./pages/Anomalies').then(m => ({ default: m.AnomaliesPage })))
const SettingsPage = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsPage })))
const UsersPage = lazy(() => import('./pages/Users').then(m => ({ default: m.UsersPage })))
const AuditLogsPage = lazy(() => import('./pages/AuditLogs').then(m => ({ default: m.AuditLogsPage })))
const IngestionRulesPage = lazy(() => import('./pages/IngestionRules').then(m => ({ default: m.IngestionRulesPage })))
const RetentionPoliciesPage = lazy(() => import('./pages/RetentionPolicies').then(m => ({ default: m.RetentionPoliciesPage })))
const MetricsPage = lazy(() => import('./pages/Metrics').then(m => ({ default: m.MetricsPage })))
const TracesPage = lazy(() => import('./pages/Traces').then(m => ({ default: m.TracesPage })))
const ServicesPage = lazy(() => import('./pages/Services').then(m => ({ default: m.ServicesPage })))
const UsageAnalyticsPage = lazy(() => import('./pages/UsageAnalytics').then(m => ({ default: m.UsageAnalyticsPage })))
const HealthChecksPage = lazy(() => import('./pages/HealthChecks').then(m => ({ default: m.HealthChecksPage })))
const PostmortemsPage = lazy(() => import('./pages/Postmortems'))

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
      <AuthProvider>
        <WorkspaceHeaderProvider>
        <TopologyHeaderProvider>
        <HelpProvider>
          <TourProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/share/e/:token" element={<PublicEventView />} />

            {/* Protected routes - wrapped in Suspense for lazy loading */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <NavigationTracker />
                    <ErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Navigate to="/events" replace />} />
                        <Route path="/events" element={<EventsPage />} />
                        <Route path="/live-tail" element={<LiveTailPage />} />
                        <Route path="/signals" element={<SignalsPage />} />
                        <Route path="/dashboards" element={<DashboardsPage />} />
                        <Route path="/dashboards/:id" element={<DashboardViewPage />} />
                        <Route path="/alerts" element={<AlertsPage />} />
                        <Route path="/anomalies" element={<AnomaliesPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route
                          path="/users"
                          element={
                            <ProtectedRoute requiredRole="Admin">
                              <UsersPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/audit"
                          element={
                            <ProtectedRoute requiredRole="Admin">
                              <AuditLogsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ingestion-rules"
                          element={<IngestionRulesPage />}
                        />
                        <Route
                          path="/retention-policies"
                          element={<RetentionPoliciesPage />}
                        />
                        <Route
                          path="/metrics"
                          element={<MetricsPage />}
                        />
                        <Route path="/traces" element={<TracesPage />} />
                        <Route path="/services" element={<ServicesPage />} />
                        <Route path="/health-checks" element={<HealthChecksPage />} />
                        <Route path="/postmortems" element={<PostmortemsPage />} />
                        <Route
                          path="/usage"
                          element={
                            <ProtectedRoute requiredRole="Admin">
                              <UsageAnalyticsPage />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </Suspense>
                    </ErrorBoundary>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Suspense fallback={null}>
            <HelpPanel />
          </Suspense>
          </TourProvider>
        </HelpProvider>
        </TopologyHeaderProvider>
        </WorkspaceHeaderProvider>
      </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}

export default App
